import { formatUnit } from '@dynatrace-sdk/units';

import { isUnit } from '../../../../../core/types/formatter.js';
import {
  TimeseriesDatapoint,
  TimeseriesUnit,
} from '../../../../../core/types/timeseries.js';

export type CumulativePoint = {
  datapointsCumulative: number;
  baseCumulative: number;
  seriesId: string;
};

export type MapStackedPoint = Map<string, CumulativePoint>;

function getUnitString(unit: TimeseriesUnit) {
  if (unit === undefined) {
    return 'undefined';
  }
  if (unit === null) {
    return 'null';
  }

  if (unit === '') {
    return '';
  }

  if (isUnit(unit)) {
    return formatUnit(unit);
  }

  return unit.toString();
}

/**
 * Gets the stacked key for the mapStackedPoints Map
 *
 * @param sign - current sign
 * @param datapoint - Current datapoint
 * @param unit - Current series unit
 * @returns - Stacked key to access the mapStackedPoints
 */
export function getStackedKeyByUnit(
  sign: number,
  datapoint: TimeseriesDatapoint,
  unit: TimeseriesUnit,
) {
  return (
    Math.sign(sign).toString() +
    datapoint.start.getTime() +
    datapoint.end?.getTime() +
    getUnitString(unit)
  );
}

/**
 * Gets the cumulative point value from the mapStackedPoints given the new value.
 *
 * @param mapStackedPoints - Given MapStackedPoint map
 * @param datapoint - Current datapoint
 * @param unit - Current series unit
 * @param seriesId - Current series id
 * @param value - Current modified value of the datapoint
 * @param sign - sign where value will be stacked
 * @returns - CumulativePoint
 */
export function getCumulativeValue(
  mapStackedPoints: MapStackedPoint,
  datapoint: TimeseriesDatapoint,
  unit: TimeseriesUnit,
  seriesId: string,
  value: number = 0,
  sign: number,
) {
  const key = getStackedKeyByUnit(sign, datapoint, unit);

  const stackedDataPoint = mapStackedPoints.get(key);

  let datapointsCumulative = 0;
  let baseCumulative = 0;
  let cumulativeValue = value;

  if (stackedDataPoint) {
    const {
      datapointsCumulative: prevDatapointsCumulative,
      baseCumulative: prevBaseCumulative,
      seriesId: prevSeriesId,
    } = stackedDataPoint;

    datapointsCumulative = prevDatapointsCumulative ?? 0;
    baseCumulative = prevBaseCumulative ?? 0;

    const baseCumulativeHasToChange = prevSeriesId !== seriesId;

    if (baseCumulativeHasToChange) {
      baseCumulative = datapointsCumulative;
    }

    cumulativeValue = baseCumulative + value;
  }

  datapointsCumulative =
    stackedDataPoint?.seriesId !== seriesId
      ? cumulativeValue
      : Math.sign(sign) > 0
      ? Math.max(cumulativeValue, datapointsCumulative)
      : Math.min(cumulativeValue, datapointsCumulative);

  return { cumulativeValue, datapointsCumulative, baseCumulative, key };
}
